import type {
	JiraSideNav,
	JiraSideNavMessageMetadata,
} from '@post-office/placement-create-pipeline/pipes';
import {
	createClientPipeline,
	createServerPipeline,
} from '@post-office/placement-create-pipeline/create-pipeline';
import { type GenerateValidationFor } from '@atlassian/vindicated';

import { type MessageTemplateContext } from '../../..';
import { type NewIconProps } from '@atlaskit/icon';
import { type TransformMetadataResult } from '@post-office/shared-contracts/message-template-metadata';
import { type AnyHydrationResult } from '@post-office/shared-contracts/placement-pipe/pipeline-types';
import { type RequestContext } from '@post-office/shared-contracts/shared-request-middleware-context';
import { type JSX } from 'react/jsx-runtime';

export type Edition = 'standard' | 'premium';
export type SuggestedFeature = 'Plans' | 'Permissions';
export type BannerMessage = 'heading' | 'cta';

export type HydrationResult = {
	edition: Edition;
	placeOrderLiteHref: string;
	suggestedFeature: SuggestedFeature;
	trialLength: number;
	hasPaymentDetails: boolean;
	changeOfferingUrl?: string;
	permissionsUrl: string | null;
	shouldForceOpenModal: boolean;
	entitlementId: string;
};

export type MessageFrontendProps = {
	onDismissed: () => void;
	edition: Edition;
	placeOrderLiteHref: string;
	suggestedFeature: SuggestedFeature;
	trialLength: number;
	hasPaymentDetails: boolean;
	changeOfferingUrl?: string;
	permissionsUrl: string | null;
	shouldForceOpenModal: boolean;
	entitlementId: string;
};

export type GetChangeOfferingUrlResponse = {
	tenantContexts: Array<{
		entitlementInfo: {
			entitlement: {
				experienceCapabilities: {
					changeOfferingV2: {
						experienceUrl: string;
					};
				};
			};
		};
	}>;
};

export type GetChangeOfferingUrlVariables = {
	cloudId: string;
	hamsProductKey: string;
	offeringName: string;
};

export type IconProp = React.ComponentType<NewIconProps>;

export type VHydrationResult = GenerateValidationFor<HydrationResult>;

type Pipe = JiraSideNav<{
	messageContext: MessageTemplateContext;
	hydrationResult: HydrationResult;
}>;

const clientPipeline = createClientPipeline<Pipe>();
export const createRender: <
	U extends (
		renderProps: Omit<HydrationResult, 'postOffice'> & {
			onDismissed: () => void;
		},
	) => JSX.Element | null,
>(
	render: U,
) => (
	renderProps: Omit<HydrationResult, 'postOffice'> & {
		onDismissed: () => void;
	},
) => JSX.Element | null = clientPipeline.createRender;
export const createValidate: (
	validate: (apiResponse: unknown) => HydrationResult,
) => (apiResponse: unknown) => HydrationResult = clientPipeline.createValidate;
const serverPipeline = createServerPipeline<Pipe>();
export const createHydrate: (
	hydrate: (
		requestContext: RequestContext,
		placementContext: Record<string, never>,
		messageContext: {
			tenantId: string;
			transactionAccountId: string;
			entitlementId: string;
			trialedEdition: Edition;
		} & {
			messageInstanceId: string;
			eventTime: string;
		},
	) => Promise<HydrationResult>,
) => (
	requestContext: RequestContext,
	placementContext: Record<string, never>,
	messageContext: {
		tenantId: string;
		transactionAccountId: string;
		entitlementId: string;
		trialedEdition: Edition;
	} & {
		messageInstanceId: string;
		eventTime: string;
	},
) => Promise<AnyHydrationResult> = serverPipeline.createHydrate;
export const createHydrateExample: (
	hydrateExample: (
		requestContext: Partial<RequestContext>,
		placementContext: Record<string, never>,
		messageContext: {
			tenantId: string;
			transactionAccountId: string;
			entitlementId: string;
			trialedEdition: Edition;
		},
	) => HydrationResult,
	config: {
		requestContext?: Partial<RequestContext>;
		additionalRenderProps: Omit<
			Omit<HydrationResult, 'postOffice'> & {
				onDismissed: () => void;
			},
			keyof HydrationResult
		>;
	},
) => (
	requestContext: Partial<RequestContext>,
	placementContext: Record<string, never>,
	messageContext: {
		tenantId: string;
		transactionAccountId: string;
		entitlementId: string;
		trialedEdition: Edition;
	} & {
		messageInstanceId: string;
		eventTime: string;
	},
) => Omit<HydrationResult, 'postOffice'> & {
	onDismissed: () => void;
} = serverPipeline.createHydrateExample;
export const createTransformMetadata: (
	transformMetadata: (params: {
		messageContext: {
			tenantId: string;
			transactionAccountId: string;
			entitlementId: string;
			trialedEdition: Edition;
		};
	}) => JiraSideNavMessageMetadata,
) => (params: {
	messageContext: {
		tenantId: string;
		transactionAccountId: string;
		entitlementId: string;
		trialedEdition: Edition;
	};
}) => TransformMetadataResult = serverPipeline.createTransformMetadata;
